/**
 * Resolves the Sampling table columns from the business unit's "Expense Inventory - Detail"
 * configuration. Missing configuration rows keep the existing all-visible behavior.
 */
export const SAMPLING_COLUMN_KEY = {
  PART: 'part',
  LOT_SERIAL: 'lotSerial',
  QTY: 'qty',
  ACTUAL_QTY: 'actualQty',
  SOURCE_LOCATION: 'sourceLocation',
  EXPIRATION: 'expiration',
} as const;

export type SamplingColumnKey = typeof SAMPLING_COLUMN_KEY[keyof typeof SAMPLING_COLUMN_KEY];
export type SamplingColumnVisibility = Record<SamplingColumnKey, boolean>;

export interface SamplingColumnRule {
  mappingKey?: string | null;
  isShow?: boolean | null;
}

export type ExpenseInventoryDetailConfiguration = Record<string, SamplingColumnRule[] | null>;

export const ALL_SAMPLING_COLUMNS_VISIBLE: SamplingColumnVisibility = {
  part: true,
  lotSerial: true,
  qty: true,
  actualQty: true,
  sourceLocation: true,
  expiration: true,
};

const SAMPLING_COLUMN_WIDTH = {
  PART: '3fr',
  BASE_OR_CAPITAL: '0.8fr',
  LOT_SERIAL: '1.2fr',
  QTY: '0.8fr',
  ACTUAL_QTY: '0.8fr',
  SOURCE_LOCATION: '1.3fr',
  EXPIRATION: '1.1fr',
  COST: '0.8fr',
  DECISION: '1.6fr',
  ACTION: '0.6fr',
};

export const getSamplingColumnVisibility = (
  configuration: ExpenseInventoryDetailConfiguration | null | undefined,
  writeOffType: string | null | undefined
): SamplingColumnVisibility => {
  if (!writeOffType) {
    return ALL_SAMPLING_COLUMNS_VISIBLE;
  }
  const rules = configuration?.[writeOffType];
  if (!rules?.length) {
    return ALL_SAMPLING_COLUMNS_VISIBLE;
  }
  const isColumnShown = (key: SamplingColumnKey): boolean =>
    rules.find(rule => rule?.mappingKey === key)?.isShow ?? true;

  return {
    part: isColumnShown(SAMPLING_COLUMN_KEY.PART),
    lotSerial: isColumnShown(SAMPLING_COLUMN_KEY.LOT_SERIAL),
    qty: isColumnShown(SAMPLING_COLUMN_KEY.QTY),
    actualQty: isColumnShown(SAMPLING_COLUMN_KEY.ACTUAL_QTY),
    sourceLocation: isColumnShown(SAMPLING_COLUMN_KEY.SOURCE_LOCATION),
    expiration: isColumnShown(SAMPLING_COLUMN_KEY.EXPIRATION),
  };
};

export const getSamplingGridTemplateColumns = (
  visibility: SamplingColumnVisibility,
  showReviewColumns: boolean
): string => {
  const tracks = [
    { width: SAMPLING_COLUMN_WIDTH.PART, isShown: visibility.part },
    { width: SAMPLING_COLUMN_WIDTH.BASE_OR_CAPITAL, isShown: visibility.part },
    { width: SAMPLING_COLUMN_WIDTH.LOT_SERIAL, isShown: visibility.lotSerial },
    { width: SAMPLING_COLUMN_WIDTH.QTY, isShown: visibility.qty },
    { width: SAMPLING_COLUMN_WIDTH.ACTUAL_QTY, isShown: visibility.actualQty },
    { width: SAMPLING_COLUMN_WIDTH.SOURCE_LOCATION, isShown: visibility.sourceLocation },
    { width: SAMPLING_COLUMN_WIDTH.EXPIRATION, isShown: visibility.expiration },
    { width: SAMPLING_COLUMN_WIDTH.COST, isShown: showReviewColumns },
    { width: SAMPLING_COLUMN_WIDTH.DECISION, isShown: showReviewColumns },
    { width: SAMPLING_COLUMN_WIDTH.ACTION, isShown: true },
  ];
  return tracks
    .filter(track => track.isShown)
    .map(track => track.width)
    .join(' ');
};
